import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Space, Table } from "antd";
import { toast } from "react-toastify";
import DashboardMessage from "../components/DashboardMessage";
import Loader from "../components/Loader";
import useFetch from "../customHooks/useFetch";
import config from "../services/config.json";
import http from "../services/httpServices";
import { getTokenSession } from "../utils/common";
import { MenuActive } from "../context/MenuActiveContext";
import "./UserForm.css";

const UserManagement = () => {
  const { setMunuActiv } = useContext(MenuActive);
  const { loading, data, error } = useFetch("dashboard/viewUsers");
  const [users, setUsers] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setMunuActiv([true, "UserManagement"]);
  }, [setMunuActiv]);

  useEffect(() => {
    if (Array.isArray(data?.users)) setUsers(data.users);
  }, [data]);

  const columns = useMemo(() => [
    {
      title: "Profile",
      dataIndex: "profile_image",
      key: "profile_image",
      render: (image, user) => (
        <img
          className="user-table-avatar"
          src={image || require("../dist/webImages/noimage.jpg")}
          alt={`${user.name || "User"} profile`}
          onError={(event) => { event.currentTarget.src = require("../dist/webImages/noimage.jpg"); }}
        />
      ),
    },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Phone", dataIndex: "phone", key: "phone", render: (phone) => phone || "—" },
    { title: "Location", dataIndex: "location", key: "location", render: (location) => location || "—" },
    { title: "Address", dataIndex: "address", key: "address", render: (address) => address || "—", ellipsis: true },
    {
      title: "Actions",
      key: "actions",
      render: (_, user) => (
        <Space>
          <Button onClick={() => navigate(`/${config.demo}users/edit/${user.id}`)}>Edit</Button>
          <Button danger loading={deletingId === user.id} onClick={() => handleDelete(user)}>Delete</Button>
        </Space>
      ),
    },
  ], [deletingId, navigate]);

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.name || user.email}?`)) return;
    setDeletingId(user.id);
    try {
      const response = await http.get(`${config.apiEndPoint}dashboard/deleteUser/${user.id}`, {
        headers: { Authorization: `Bearer ${getTokenSession()}` },
      });
      if (response.data?.status === "false" || response.data?.status === "error") {
        toast.error(response.data.message || "Could not delete user");
        return;
      }
      setUsers((current) => current.filter((item) => item.id !== user.id));
      toast.success(response.data?.message || "User deleted");
    } catch (requestError) {
      toast.error(requestError.response?.data?.message || "Could not delete user");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading && !data && !error) return <Loader />;

  return (
    <>
      <DashboardMessage />
      <div className="flex items-center justify-between gap-4 mb-4">
        <h1 className="text-xl font-semibold">User Management</h1>
        <Link className="btn btn-primary" to={`/${config.demo}users/create`}>Create User</Link>
      </div>
      {error && <p role="alert">Unable to load users. Please refresh and try again.</p>}
      <div className="tablereact rounded-sm my-5 border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">
        <Table rowKey="id" columns={columns} dataSource={users} loading={loading} scroll={{ x: "max-content" }} />
      </div>
    </>
  );
};

export default UserManagement;
